import { useState, useEffect, useRef } from "react";
import DoxzonLogo from "../assets/doxzon-logo.png";

const Icon = ({ d, size = 10 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: size, height: size, display: "block" }}
  >
    <path d={d} />
  </svg>
);

const IC = {
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  layout: "M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z",
  users:
    "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  calendar: "M3 4h18v18H3zM16 2v4M8 2v4M3 10h18",
  bar: "M18 20V10M12 20V4M6 20v-6",
  bell: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0",
  msg: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  pill: "M10.5 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM2 12h2M20 12h2M12 2v2M12 20v2",
  activity: "M22 12h-4l-3 9L9 3l-3 9H2",
  trending: "M23 6l-9.5 9.5-5-5L1 18M17 6h6v6",
  user: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z",
  clipboard:
    "M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 2h6v4H9z",
  dollar: "M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  check: "M20 6L9 17l-5-5",
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

function useDarkMode() {
  const [dark, setDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark",
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setDark(document.documentElement.getAttribute("data-theme") === "dark"),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

const STAT_CARDS = [
  { icon: "users", num: "184", label: "Patients today" },
  { icon: "clipboard", num: "26", label: "Pending Rx" },
  { icon: "activity", num: "98%", label: "Care quality" },
  { icon: "trending", num: "+32%", label: "MoM growth" },
];

const TOP_STATS = [
  {
    label: "Total Patients",
    value: "2,543",
    sub: "+4.2% · May 12–18",
    color: "#14b8a6",
    bg: "#e6faf5",
    icon: "activity",
  },
  {
    label: "Appointments",
    value: "320",
    sub: "+0.8% · May 12–18",
    color: "#3b82f6",
    bg: "#eff6ff",
    icon: "calendar",
  },
  {
    label: "New Patients",
    value: "156",
    sub: "+6.1% · May 12–18",
    color: "#10b981",
    bg: "#ecfdf5",
    icon: "users",
  },
  {
    label: "Revenue",
    value: "$48,920",
    sub: "+2.4% · May 12–18",
    color: "#8b5cf6",
    bg: "#f5f3ff",
    icon: "dollar",
  },
];
const TOP_STATS_DARK_BG = ["#0d2e28", "#0d1f3a", "#0a2a1a", "#1e1030"];

const NAV_ITEMS = [
  { icon: "layout", label: "Dashboard", active: true },
  { icon: "user", label: "Patients", active: false },
  { icon: "calendar", label: "Appointments", active: false },
  { icon: "bar", label: "Analytics", active: false },
  { icon: "bar", label: "Reports", active: false },
  { icon: "bell", label: "Alerts", active: false },
  { icon: "msg", label: "Messages", active: false },
  { icon: "pill", label: "Medications", active: false },
  { icon: "settings", label: "Settings", active: false },
];

const PATIENTS = [
  {
    name: "B / Male",
    gender: "Male",
    dob: "May 18, 2026",
    cond: "Hypertension",
    cc: "#ef4444",
    cb: "#fee2e2",
    prov: "Dr. Sarah Johnson",
    pi: "SJ",
    status: "Completed",
    sc: "#dcfce7",
    st: "#15803d",
  },
  {
    name: "D / Female",
    gender: "Female",
    dob: "May 17, 2026",
    cond: "Diabetes Type II",
    cc: "#3b82f6",
    cb: "#dbeafe",
    prov: "Dr. Michael Lee",
    pi: "ML",
    status: "Scheduled",
    sc: "#dbeafe",
    st: "#1d4ed8",
  },
  {
    name: "J / Male",
    gender: "Male",
    dob: "May 16, 2026",
    cond: "Asthma",
    cc: "#b45309",
    cb: "#fef9c3",
    prov: "Dr. Emily Davis",
    pi: "ED",
    status: "In Progress",
    sc: "#fef3c7",
    st: "#92400e",
  },
  {
    name: "R / Female",
    gender: "Female",
    dob: "May 15, 2026",
    cond: "Arthritis",
    cc: "#ef4444",
    cb: "#fee2e2",
    prov: "Dr. Sarah Johnson",
    pi: "SJ",
    status: "Completed",
    sc: "#dcfce7",
    st: "#15803d",
  },
];
const PATIENTS_DARK = [
  { cc: "#f87171", cb: "rgba(239,68,68,0.18)" },
  { cc: "#60a5fa", cb: "rgba(59,130,246,0.18)" },
  { cc: "#fbbf24", cb: "rgba(180,83,9,0.18)" },
  { cc: "#f87171", cb: "rgba(239,68,68,0.18)" },
];
const STATUS_DARK = [
  { sc: "rgba(21,128,61,0.20)", st: "#4ade80" },
  { sc: "rgba(29,78,216,0.20)", st: "#60a5fa" },
  { sc: "rgba(146,64,14,0.20)", st: "#fbbf24" },
  { sc: "rgba(21,128,61,0.20)", st: "#4ade80" },
];

function Sparkline({ dark }) {
  const p1 = [
    46, 34, 40, 26, 32, 18, 25, 15, 21, 19, 11, 17, 13, 16, 8, 13, 6, 11, 19,
    10, 15, 8, 11, 9,
  ];
  const W = 320,
    H = 62;
  const xs = p1.map((_, i) => (i / (p1.length - 1)) * W);
  const l1 = xs
    .map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${p1[i]}`)
    .join(" ");
  const p2 = [
    52, 44, 48, 36, 40, 28, 34, 24, 30, 27, 19, 25, 21, 24, 16, 21, 14, 19, 27,
    18, 23, 16, 19, 17,
  ];
  const l2 = xs
    .map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${p2[i]}`)
    .join(" ");
  const area = `${l1} L${W},${H} L0,${H} Z`;
  const labels = [
    "May 12",
    "May 13",
    "May 14",
    "May 15",
    "May 16",
    "May 17",
    "May 18",
  ];
  const lineColor = dark ? "#c8961e" : "#14b8a6";
  const labelColor = dark ? "#7a5c10" : "#94a3b8";
  const legendText = dark ? "#b89a4a" : "#64748b";
  const legendDash = dark ? "#5a4010" : "#cbd5e1";
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 62 }}>
        <defs>
          <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.22" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#sg)" />
        <path
          d={l1}
          fill="none"
          stroke={lineColor}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d={l2}
          fill="none"
          stroke={dark ? "#5a4010" : "#94a3b8"}
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="3,2"
        />
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 2,
        }}
      >
        {labels.map((l) => (
          <span key={l} style={{ fontSize: 6, color: labelColor }}>
            {l}
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span
            style={{
              width: 12,
              height: 6,
              borderRadius: 3,
              background: lineColor,
              display: "inline-block",
            }}
          />
          <span style={{ fontSize: 6, color: legendText }}>This Period</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span
            style={{
              width: 12,
              height: 6,
              borderRadius: 3,
              background: legendDash,
              display: "inline-block",
            }}
          />
          <span style={{ fontSize: 6, color: legendText }}>Last Period</span>
        </div>
      </div>
    </div>
  );
}

function Donut({ dark }) {
  const segs = [
    {
      label: "Chronic Conditions",
      pct: 36,
      color: dark ? "#c8961e" : "#14b8a6",
    },
    { label: "Preventive Care", pct: 27, color: dark ? "#f0c040" : "#38bdf8" },
    { label: "Acute Care", pct: 22, color: dark ? "#a78bfa" : "#a78bfa" },
    { label: "Follow-up Care", pct: 10, color: dark ? "#fb923c" : "#fb923c" },
    { label: "Other", pct: 5, color: dark ? "#3d2e10" : "#e2e8f0" },
  ];
  const r = 28,
    cx = 40,
    cy = 40,
    circ = 2 * Math.PI * r;
  let off = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <svg viewBox="0 0 80 80" style={{ width: 76, height: 76, flexShrink: 0 }}>
        {segs.map(({ pct, color }, i) => {
          const dash = (pct / 100) * circ;
          const el = (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={color}
              strokeWidth="13"
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-off}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          );
          off += dash;
          return el;
        })}
        <text
          x="40"
          y="37"
          textAnchor="middle"
          fontSize="7.5"
          fontWeight="700"
          fill={dark ? "#f5e6b0" : "#0f172a"}
        >
          2,543
        </text>
        <text
          x="40"
          y="47"
          textAnchor="middle"
          fontSize="5.5"
          fill={dark ? "#b89a4a" : "#94a3b8"}
        >
          Total
        </text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {segs.map(({ label, pct, color }) => (
          <div
            key={label}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 6,
                color: dark ? "#b89a4a" : "#475569",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </span>
            <span
              style={{
                fontSize: 6,
                color: dark ? "#7a5c10" : "#94a3b8",
                marginLeft: "auto",
                paddingLeft: 4,
              }}
            >
              {pct}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarChart({ dark }) {
  const data = [
    { label: "Cardiology", v: 210 },
    { label: "Neurology", v: 188 },
    { label: "Pediatrics", v: 165 },
    { label: "Orthopedics", v: 129 },
    { label: "Radiology", v: 80 },
    { label: "Nephro.", v: 45 },
  ];
  const max = 210;
  const barActive = dark ? "#c8961e" : "#14b8a6";
  const barRest = dark ? "#3d2e10" : "#bfece6";
  return (
    <div
      style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 72 }}
    >
      {data.map(({ label, v }, i) => (
        <div
          key={label}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            flex: 1,
          }}
        >
          <div
            style={{
              width: "100%",
              height: `${(v / max) * 60}px`,
              background: i === 0 ? barActive : barRest,
              borderRadius: "2px 2px 0 0",
            }}
          />
          <span
            style={{
              fontSize: 5.5,
              color: dark ? "#7a5c10" : "#94a3b8",
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  );
}

function MiniDashboard({ dark }) {
  const s = dark
    ? {
        pageBg: "#0a0800",
        sidebarBg: "#0f0b02",
        sidebarBord: "#2a1f04",
        navActive: "#c8961e",
        navInactive: "#7a5c10",
        upgradeBg: "#150f02",
        upgradeBord: "#2a1e04",
        upgradeHead: "#f0c040",
        upgradeSub: "#b89a4a",
        upgradeBtn: "#c8961e",
        topbarBg: "#0f0b02",
        topbarBord: "#2a1f04",
        welcomeText: "#f5e6b0",
        subText: "#7a5c10",
        datePill: "#2a1f04",
        datePillBord: "#3d2e10",
        datePillText: "#b89a4a",
        avatarBg: "#c8961e",
        cardBg: "#110d02",
        cardBord: "#2a1f04",
        titleText: "#f0c040",
        dropdownBord: "#3d2e10",
        dropdownText: "#7a5c10",
        tableBord: "#1e1704",
        tableRowBord: "#150f02",
        tableHead: "#7a5c10",
        valueText: "#f5e6b0",
        provText: "#b89a4a",
        provIconBg: "#2a1f04",
        provIconText: "#b89a4a",
        viewAll: "#f0c040",
        patientIconBg: "#1a3020",
        patientIconTx: "#4ade80",
      }
    : {
        pageBg: "#f8fafc",
        sidebarBg: "#fff",
        sidebarBord: "#f1f5f9",
        navActive: "#14b8a6",
        navInactive: "#94a3b8",
        upgradeBg: "#f0fdf9",
        upgradeBord: "#ccfbf1",
        upgradeHead: "#0f766e",
        upgradeSub: "#5eead4",
        upgradeBtn: "#14b8a6",
        topbarBg: "#fff",
        topbarBord: "#f1f5f9",
        welcomeText: "#0f172a",
        subText: "#94a3b8",
        datePill: "#fff",
        datePillBord: "#e2e8f0",
        datePillText: "#94a3b8",
        avatarBg: "#14b8a6",
        cardBg: "#fff",
        cardBord: "#f1f5f9",
        titleText: "#334155",
        dropdownBord: "#f1f5f9",
        dropdownText: "#94a3b8",
        tableBord: "#f1f5f9",
        tableRowBord: "#f8fafc",
        tableHead: "#94a3b8",
        valueText: "#334155",
        provText: "#64748b",
        provIconBg: "#f1f5f9",
        provIconText: "#64748b",
        viewAll: "#14b8a6",
        patientIconBg: "#ccfbf1",
        patientIconTx: "#0f766e",
      };

  return (
    <div
      style={{
        display: "flex",
        background: s.pageBg,
        fontFamily: "inherit",
        transition: "background 0.4s",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          width: 130,
          background: s.sidebarBg,
          borderRight: `1px solid ${s.sidebarBord}`,
          display: "flex",
          flexDirection: "column",
          padding: "12px 8px",
          gap: 2,
          flexShrink: 0,
          transition: "background 0.4s",
        }}
      >
        <div
  style={{
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "12px 8px",
    marginBottom: "8px",
  }}
>
 <img
  src={DoxzonLogo}
  alt="Doxzon"
  className="w-16 h-auto object-contain"   
/>
</div>
        {NAV_ITEMS.map(({ icon, label, active }) => (
          <div
            key={label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 8px",
              borderRadius: 8,
              cursor: "default",
              background: active ? s.navActive : "transparent",
              color: active ? "#fff" : s.navInactive,
              transition: "background 0.3s, color 0.3s",
            }}
          >
            <Icon d={IC[icon]} size={10} />
            <span style={{ fontSize: 8, lineHeight: 1 }}>{label}</span>
          </div>
        ))}
        <div
          style={{
            marginTop: 12,
            borderRadius: 12,
            background: s.upgradeBg,
            border: `1px solid ${s.upgradeBord}`,
            padding: "10px 8px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: s.upgradeBtn,
              display: "grid",
              placeItems: "center",
              margin: "0 auto 6px",
              color: "#fff",
            }}
          >
            <Icon d={IC.shield} size={10} />
          </div>
          <p
            style={{
              fontSize: 7,
              fontWeight: 700,
              color: s.upgradeHead,
              margin: 0,
            }}
          >
            Upgrade to Pro
          </p>
          <p style={{ fontSize: 6, color: s.upgradeSub, margin: "2px 0 6px" }}>
            Unlock advanced analytics
          </p>
          <button
            style={{
              width: "100%",
              background: s.upgradeBtn,
              color: "#fff",
              border: "none",
              borderRadius: 6,
              fontSize: 6.5,
              fontWeight: 700,
              padding: "4px 0",
              cursor: "default",
            }}
          >
            Upgrade Now
          </button>
        </div>
      </div>

      {/* Main */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            background: s.topbarBg,
            borderBottom: `1px solid ${s.topbarBord}`,
            transition: "background 0.4s",
          }}
        >
          <div>
            <p
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: s.welcomeText,
                margin: 0,
              }}
            >
              Welcome back, Dr. Sarah Johnson 👋
            </p>
            <p style={{ fontSize: 7, color: s.subText, margin: 0 }}>
              Here's what's happening today.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: s.avatarBg,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontSize: 7,
                fontWeight: 700,
              }}
            >
              SJ
            </div>
          </div>
        </div>

        <div
          style={{
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 8,
            }}
          >
            {TOP_STATS.map(({ label, value, sub, color, icon }, idx) => (
              <div
                key={label}
                style={{
                  background: s.cardBg,
                  borderRadius: 12,
                  border: `1px solid ${s.cardBord}`,
                  padding: "8px 10px",
                  transition: "background 0.4s",
                }}
              >
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 8,
                    background: dark
                      ? TOP_STATS_DARK_BG[idx]
                      : TOP_STATS[idx].bg,
                    display: "grid",
                    placeItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <span style={{ color }}>
                    <Icon d={IC[icon]} size={10} />
                  </span>
                </div>
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: s.welcomeText,
                    margin: 0,
                    lineHeight: 1,
                  }}
                >
                  {value}
                </p>
                <p
                  style={{ fontSize: 6.5, color: s.subText, margin: "2px 0 0" }}
                >
                  {label}
                </p>
                <p
                  style={{
                    fontSize: 6.5,
                    color,
                    fontWeight: 600,
                    margin: "1px 0 0",
                  }}
                >
                  {sub}
                </p>
              </div>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.7fr 1fr 1fr",
              gap: 8,
            }}
          >
            {[
              {
                title: "Patient Visits",
                badge: "Daily ▾",
                content: <Sparkline dark={dark} />,
              },
              {
                title: "Patient Breakdown",
                badge: "All Time ▾",
                content: <Donut dark={dark} />,
              },
              {
                title: "Visits by Department",
                badge: "This Period ▾",
                content: <BarChart dark={dark} />,
              },
            ].map(({ title, badge, content }) => (
              <div
                key={title}
                style={{
                  background: s.cardBg,
                  borderRadius: 12,
                  border: `1px solid ${s.cardBord}`,
                  padding: "10px",
                  transition: "background 0.4s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{ fontSize: 8, fontWeight: 600, color: s.titleText }}
                  >
                    {title}
                  </span>
                  <span
                    style={{
                      fontSize: 6.5,
                      color: s.dropdownText,
                      border: `1px solid ${s.dropdownBord}`,
                      borderRadius: 4,
                      padding: "1px 5px",
                    }}
                  >
                    {badge}
                  </span>
                </div>
                {content}
              </div>
            ))}
          </div>

          <div
            style={{
              background: s.cardBg,
              borderRadius: 12,
              border: `1px solid ${s.cardBord}`,
              padding: "10px",
              transition: "background 0.4s",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span
                style={{ fontSize: 8, fontWeight: 600, color: s.titleText }}
              >
                Recent Patients
              </span>
              <span
                style={{
                  fontSize: 7,
                  color: s.viewAll,
                  fontWeight: 500,
                  cursor: "default",
                }}
              >
                View All →
              </span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${s.tableBord}` }}>
                  {[
                    "Name",
                    "Gender",
                    "Last Visit",
                    "Condition",
                    "Provider",
                    "Status",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        fontSize: 6.5,
                        color: s.tableHead,
                        fontWeight: 500,
                        paddingBottom: 4,
                        paddingRight: 8,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PATIENTS.map((p, i) => {
                  const dc = dark ? PATIENTS_DARK[i] : { cc: p.cc, cb: p.cb };
                  const ds = dark ? STATUS_DARK[i] : { sc: p.sc, st: p.st };
                  return (
                    <tr
                      key={i}
                      style={{
                        borderBottom:
                          i < PATIENTS.length - 1
                            ? `1px solid ${s.tableRowBord}`
                            : "none",
                      }}
                    >
                      <td style={{ padding: "4px 8px 4px 0" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              background: s.patientIconBg,
                              color: s.patientIconTx,
                              fontSize: 6,
                              fontWeight: 700,
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            {p.name[0]}
                          </span>
                          <span
                            style={{
                              fontSize: 7,
                              fontWeight: 500,
                              color: s.valueText,
                            }}
                          >
                            {p.name}
                          </span>
                        </div>
                      </td>
                      <td
                        style={{
                          fontSize: 7,
                          color: s.subText,
                          paddingRight: 8,
                        }}
                      >
                        {p.gender}
                      </td>
                      <td
                        style={{
                          fontSize: 7,
                          color: s.subText,
                          paddingRight: 8,
                        }}
                      >
                        {p.dob}
                      </td>
                      <td style={{ paddingRight: 8 }}>
                        <span
                          style={{
                            fontSize: 6.5,
                            fontWeight: 600,
                            color: dc.cc,
                            background: dc.cb,
                            borderRadius: 4,
                            padding: "1px 5px",
                          }}
                        >
                          {p.cond}
                        </span>
                      </td>
                      <td style={{ paddingRight: 8 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}
                        >
                          <span
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              background: s.provIconBg,
                              color: s.provIconText,
                              fontSize: 5,
                              fontWeight: 600,
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                            }}
                          >
                            {p.pi}
                          </span>
                          <span style={{ fontSize: 7, color: s.provText }}>
                            {p.prov}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            fontSize: 6,
                            fontWeight: 600,
                            background: ds.sc,
                            color: ds.st,
                            borderRadius: 999,
                            padding: "2px 6px",
                          }}
                        >
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function FloatingCard({ visible, dark, isMobile }) {
  return (
    <>
      <style>{`
        @keyframes floatUpDown {
          0%   { transform: translateY(0px); }
          50%  { transform: translateY(-8px); }
          100% { transform: translateY(0px); }
        }
        .float-card-anim { animation: floatUpDown 3.5s ease-in-out infinite; }
      `}</style>
      <div
        className={visible ? "float-card-anim" : ""}
        style={{
          position: "absolute",
          /* On mobile: centered below the browser. On desktop: left overhang. */
          ...(isMobile
            ? { right: 0, bottom: -20, width: 200 }
            : { left: -28, bottom: -32, width: 240 }),
          zIndex: 5,
          background: dark ? "rgba(20,14,2,0.82)" : "rgba(255,255,255,0.18)",
          backdropFilter: "blur(24px) saturate(180%)",
          WebkitBackdropFilter: "blur(24px) saturate(180%)",
          borderRadius: 20,
          border: dark
            ? "1.5px solid rgba(200,150,30,0.40)"
            : "1.5px solid rgba(255,255,255,0.45)",
          boxShadow: dark
            ? "0 20px 60px rgba(200,150,30,0.14), 0 8px 32px rgba(0,0,0,0.30)"
            : "0 20px 60px rgba(20,184,166,0.18), 0 8px 32px rgba(0,0,0,0.10)",
          padding: "14px 16px",
          opacity: visible ? 1 : 0,
          transition:
            "opacity 0.55s cubic-bezier(.22,1,.36,1) 0.65s, background 0.4s",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: dark
                ? "linear-gradient(135deg,#c8961e,#7a5c10)"
                : "linear-gradient(135deg,#2dd4bf,#0d9488)",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ width: 11, height: 11 }}
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
          <p
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: dark ? "#f0c040" : "#0d9488",
              margin: 0,
            }}
          >
            New Appointment
          </p>
        </div>
        <p
          style={{
            fontSize: 16,
            fontWeight: 800,
            color: dark ? "#f5e6b0" : "#0f172a",
            margin: 0,
            lineHeight: 1.25,
          }}
        >
          Sarah Khan · 14:30
        </p>
        <p
          style={{
            fontSize: 11,
            color: dark ? "#b89a4a" : "#64748b",
            margin: "3px 0 0",
          }}
        >
          Follow-up · Cardiology
        </p>
      </div>
    </>
  );
}

export default function DoxzonLanding() {
  const dark = useDarkMode();
  const isMobile = useIsMobile(768);
  const [visible, setVisible] = useState(false);
  const [browserVisible, setBrowserVisible] = useState(false);
  const [leftVisible, setLeftVisible] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setLeftVisible(true), 100);
    const t2 = setTimeout(() => setBrowserVisible(true), 250);
    const t3 = setTimeout(() => setVisible(true), 400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const l = dark
    ? {
        sectionBg: "#0a0800",
        badgeBg: "rgba(200,150,30,0.12)",
        badgeDot: "#f0c040",
        badgeText: "#f0c040",
        heading: "#f5e6b0",
        subText: "#b89a4a",
        cardBg: "#110d02",
        cardBorder: "rgba(200,150,30,0.18)",
        cardShadow: "0 1px 4px rgba(0,0,0,0.30)",
        iconBg: "rgba(200,150,30,0.12)",
        iconColor: "#f0c040",
        statNum: "#f5e6b0",
        statLabel: "#b89a4a",
        liveBadge: "#7a5c10",
        glowBg: "rgba(200,150,30,0.10)",
        browserBg: "#0f0b02",
        browserBord: "rgba(200,150,30,0.20)",
        browserShadow: "0 24px 56px rgba(0,0,0,0.40)",
        titlebarBg: "#110d02",
        titlebarBord: "rgba(200,150,30,0.14)",
        urlText: "#7a5c10",
        dot3: "#c8961e",
      }
    : {
        sectionBg: "#edf6f9",
        badgeBg: "rgba(20,184,166,0.10)",
        badgeDot: "#14b8a6",
        badgeText: "#0d9488",
        heading: "#0f172a",
        subText: "#64748b",
        cardBg: "#fff",
        cardBorder: "rgba(148,163,184,0.18)",
        cardShadow: "0 1px 4px rgba(0,0,0,0.05)",
        iconBg: "#f0fdf9",
        iconColor: "#14b8a6",
        statNum: "#0f172a",
        statLabel: "#94a3b8",
        liveBadge: "#94a3b8",
        glowBg: "rgba(94,234,212,0.18)",
        browserBg: "#fff",
        browserBord: "#e2e8f0",
        browserShadow: "0 24px 56px rgba(0,0,0,0.10)",
        titlebarBg: "#f8fafc",
        titlebarBord: "#f1f5f9",
        urlText: "#94a3b8",
        dot3: "#2dd4bf",
      };

  return (
    <section
      style={{
        background: l.sectionBg,
        fontFamily: "'DM Sans','Outfit',system-ui,sans-serif",
        padding: isMobile ? "48px 0 120px" : "80px 0 100px",
        minHeight: "100vh",
        overflowX:
          "hidden" /* prevent any child from causing horizontal scroll */,
        transition: "background 0.4s",
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: isMobile ? "0 20px" : "0 24px",
          boxSizing: "border-box",
          width: "100%",
        }}
      >
       
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1.55fr",
            gap: isMobile ? 32 : 64,
            alignItems: "center",
          }}
        >
          <div
            style={{
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.6s ease, transform 0.6s ease",
              minWidth: 0,
              width: "100%",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: l.badgeBg,
                borderRadius: 999,
                padding: "4px 14px",
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                color: l.badgeText,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: l.badgeDot,
                  display: "inline-block",
                }}
              />
              Clinical Dashboard
            </span>

            <h2
              style={{
                fontSize: isMobile ? "1.45rem" : "2.75rem",
                fontWeight: 500,
                color: l.heading,
                lineHeight: 1.15,
                margin: "18px 0 0",
                letterSpacing: "-0.02em",
                transition: "color 0.4s",
              }}
            >
              One workspace for
              <br />
              every clinical decision.
            </h2>

            <p
              style={{
                fontSize: 14,
                lineHeight: 1.7,
                color: l.subText,
                maxWidth: isMobile ? "100%" : 420,
                margin: "14px 0 0",
                transition: "color 0.4s",
              }}
            >
              Whether you're seeking care, providing care, or enabling care,
              DOXZON brings the entire healthcare community together through one
              connected platform.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: isMobile ? 12 : 16,
                marginTop: isMobile ? 24 : 32,
              }}
            >
              {STAT_CARDS.map(({ icon, num, label }, idx) => (
                <div
                  key={label}
                  style={{
                    background: l.cardBg,
                    borderRadius: 20,
                    border: `1px solid ${l.cardBorder}`,
                    boxShadow: l.cardShadow,
                    padding: isMobile ? "14px 16px 16px" : "16px 20px 20px",
                    opacity: leftVisible ? 1 : 0,
                    transform: leftVisible
                      ? "translateY(0)"
                      : "translateY(20px)",
                    transition: `opacity 0.5s ease ${0.1 + idx * 0.08}s, transform 0.5s ease ${0.1 + idx * 0.08}s, background 0.4s`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 12,
                        background: l.iconBg,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <span style={{ color: l.iconColor }}>
                        <Icon d={IC[icon]} size={16} />
                      </span>
                    </span>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        color: l.liveBadge,
                      }}
                    >
                      Live
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: isMobile ? "1.2rem" : "2rem",
                      fontWeight: 500,
                      color: l.statNum,
                      margin: "10px 0 0",
                      lineHeight: 1,
                      transition: "color 0.4s",
                    }}
                  >
                    {num}
                  </p>
                  <p
                    style={{
                      fontSize: 13,
                      color: l.statLabel,
                      margin: "6px 0 0",
                      transition: "color 0.4s",
                    }}
                  >
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT COLUMN (browser mockup) ── */}
          <div
            style={{
              position: "relative",
              paddingBottom: isMobile ? 60 : 52,
              paddingLeft: isMobile ? 0 : 32,
              minWidth: 0 /* prevent grid blowout */,
              width: "100%",
            }}
          >
            {/* Glow blob */}
            <div
              style={{
                position: "absolute",
                inset: -20,
                borderRadius: "3rem",
                background: l.glowBg,
                filter: "blur(40px)",
                pointerEvents: "none",
                transition: "background 0.4s",
              }}
            />

            {/* Browser frame */}
            <div
              style={{
                position: "relative",
                zIndex: 1,
                border: `1px solid ${l.browserBord}`,
                boxShadow: l.browserShadow,
                borderRadius: isMobile ? 16 : 24,
                background: l.browserBg,
                overflow: "hidden",
                maxHeight: isMobile
                  ? 320
                  : 520 /* clip dashboard — no blank space below */,
                opacity: browserVisible ? 1 : 0,
                transform: browserVisible
                  ? "translateY(0)"
                  : "translateY(28px)",
                transition:
                  "opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1), background 0.4s",
              }}
            >
              {/* Title bar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: l.titlebarBg,
                  borderBottom: `1px solid ${l.titlebarBord}`,
                  padding: "10px 16px",
                  transition: "background 0.4s",
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "rgba(248,113,113,0.8)",
                  }}
                />
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "rgba(250,204,21,0.8)",
                  }}
                />
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: l.dot3,
                  }}
                />
                <div
                  style={{ flex: 1, display: "flex", justifyContent: "center" }}
                >
                  <span
                    style={{
                      background: dark ? "#1a1204" : "#fff",
                      border: `1px solid ${dark ? "#2a1f04" : "#e2e8f0"}`,
                      borderRadius: 6,
                      padding: "2px 12px",
                      fontSize: 11,
                      color: l.urlText,
                      fontWeight: 500,
                    }}
                  >
                    app.doxzon.com/clinic
                  </span>
                </div>
              </div>

              {/*
                Dashboard content scaling strategy:
                - Mobile: scale(0.52) with corrected wrapper width so it fills the frame
                - Desktop: no transform, renders at natural size
              */}
              <div
                style={
                  isMobile
                    ? {
                        transform: "scale(0.6)",
                        transformOrigin: "top left",
                        width: `${(1 / 0.6) * 100}%` ,
                        pointerEvents: "none",
                      }
                    : {}
                }
              >
                <MiniDashboard dark={dark} />
              </div>
            </div>

            {/* Floating appointment card */}
            <FloatingCard visible={visible} dark={dark} isMobile={isMobile} />
          </div>
        </div>
      </div>
    </section>
  );
}